import { useState, useEffect, useCallback } from 'react';
import { useTenant } from '../contexts/TenantContext';
import api from '../services/api';

// Types
interface BatchProgress {
  phase: string;
  totalImages: number;
  processed: number;
  backedUp: number;
  skipped: number;
  errors: number;
  currentImage?: string;
  percentage: number;
}

interface BatchJob {
  jobId: string;
  tenantId: string;
  status: 'pending' | 'backing_up' | 'processing' | 'completed' | 'completed_with_errors' | 'failed' | 'cancelled';
  progress: BatchProgress;
  startedAt: string;
  finishedAt?: string;
  error?: string;
  errorDetails?: Array<{ image: string; error: string }>;
}

interface PreviewData {
  tenantId: string;
  prefix: string;
  images: string[];
  totalCount: number;
  alreadyProcessed: number;
  toProcess: number;
  modifiedSinceProcessed: number;
}

interface BatchOptions {
  limit?: number;
  force: boolean;
  skipBackup: boolean;
  detectChanges: boolean;
}

export default function PhotoRetouching() {
  const { tenant } = useTenant();
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [currentJob, setCurrentJob] = useState<BatchJob | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serviceAvailable, setServiceAvailable] = useState<boolean | null>(null);

  const [options, setOptions] = useState<BatchOptions>({
    limit: undefined,
    force: false,
    skipBackup: false,
    detectChanges: true,
  });

  // Check service health
  const checkServiceHealth = useCallback(async () => {
    try {
      const health = await api.get('/photo-retouching/health');
      setServiceAvailable(health.available);
      return health.available;
    } catch {
      setServiceAvailable(false);
      return false;
    }
  }, []);

  // Load preview data
  const loadPreview = useCallback(async () => {
    if (!tenant?.id) return;

    try {
      setIsLoading(true);
      setError(null);
      const data = await api.get('/photo-retouching/batch/preview');
      setPreviewData(data);
    } catch (err: any) {
      console.error('Error loading preview:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des images');
    } finally {
      setIsLoading(false);
    }
  }, [tenant?.id]);

  // Start batch job
  const startBatch = async () => {
    if (!tenant?.id) return;

    try {
      setIsStarting(true);
      setError(null);
      const response = await api.post('/photo-retouching/batch/start', {
        limit: options.limit || undefined,
        force: options.force,
        skipBackup: options.skipBackup,
        detectChanges: options.detectChanges,
      });

      // Fetch initial status
      const status = await api.get(`/photo-retouching/batch/${response.jobId}/status`);
      setCurrentJob(status);
    } catch (err: any) {
      console.error('Error starting batch:', err);
      setError(err.response?.data?.message || 'Erreur lors du demarrage du traitement');
    } finally {
      setIsStarting(false);
    }
  };

  // Cancel job
  const cancelBatch = async () => {
    if (!currentJob) return;

    try {
      await api.post(`/photo-retouching/batch/${currentJob.jobId}/cancel`);
      // Status will be updated by polling
    } catch (err: any) {
      console.error('Error cancelling batch:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'annulation');
    }
  };

  // Poll job status
  const pollJobStatus = useCallback(async () => {
    if (!currentJob || ['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(currentJob.status)) {
      return;
    }

    try {
      const status = await api.get(`/photo-retouching/batch/${currentJob.jobId}/status`);
      setCurrentJob(status);

      // If job finished, refresh preview
      if (['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(status.status)) {
        loadPreview();
      }
    } catch (err) {
      console.error('Error polling status:', err);
    }
  }, [currentJob, loadPreview]);

  // Initial load
  useEffect(() => {
    checkServiceHealth();
    loadPreview();
  }, [checkServiceHealth, loadPreview]);

  // Poll every 3 seconds when job is active
  useEffect(() => {
    if (!currentJob || ['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(currentJob.status)) {
      return;
    }

    const interval = setInterval(pollJobStatus, 3000);
    return () => clearInterval(interval);
  }, [currentJob, pollJobStatus]);

  // Calculate elapsed time
  const getElapsedTime = () => {
    if (!currentJob) return '';
    const start = new Date(currentJob.startedAt).getTime();
    const end = currentJob.finishedAt
      ? new Date(currentJob.finishedAt).getTime()
      : Date.now();
    const seconds = Math.floor((end - start) / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'completed_with_errors':
        return 'bg-orange-100 text-orange-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      case 'processing':
      case 'backing_up':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  // Status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'En attente';
      case 'backing_up':
        return 'Sauvegarde en cours';
      case 'processing':
        return 'Traitement en cours';
      case 'completed':
        return 'Termine';
      case 'completed_with_errors':
        return 'Termine avec erreurs';
      case 'failed':
        return 'Echec';
      case 'cancelled':
        return 'Annule';
      default:
        return status;
    }
  };

  const isJobActive = currentJob !== null && !['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(currentJob.status);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Retouche Photos par Lot</h1>
        <p className="mt-1 text-gray-600">
          Traitement automatique des images produits: detourage IA, amelioration, normalisation
        </p>
      </div>

      {/* Service Status */}
      {serviceAvailable === false && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <span className="text-red-600">Le service de retouche photo n'est pas disponible.</span>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-red-800">{error}</span>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* Preview Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Apercu des images</h2>

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : previewData ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-gray-900">{previewData.totalCount}</div>
                <div className="text-sm text-gray-600 mt-1">images dans le stockage</div>
              </div>
            </div>

            <p className="text-sm text-gray-500 text-center">
              Le nombre exact d'images a traiter sera determine au demarrage du job
              (les images deja traitees seront automatiquement ignorees).
            </p>

            {previewData.images.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Echantillon d'images:</p>
                <div className="text-xs font-mono bg-gray-50 p-2 rounded max-h-24 overflow-y-auto">
                  {previewData.images.map((img, i) => (
                    <div key={i} className="truncate">{img.split('/').pop()}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-500">Aucune donnee disponible</p>
        )}

        <button
          onClick={loadPreview}
          disabled={isLoading}
          className="mt-4 text-sm text-emerald-600 hover:text-emerald-700"
        >
          Rafraichir l'apercu
        </button>
      </div>

      {/* Options */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Options de traitement</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Limiter le nombre d'images
            </label>
            <input
              type="number"
              min="1"
              placeholder="Toutes les images"
              value={options.limit || ''}
              onChange={(e) =>
                setOptions({ ...options, limit: e.target.value ? parseInt(e.target.value) : undefined })
              }
              disabled={isJobActive}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={options.detectChanges}
                onChange={(e) => setOptions({ ...options, detectChanges: e.target.checked })}
                disabled={isJobActive}
                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm text-gray-700">Detecter les images modifiees (date)</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={options.force}
                onChange={(e) => setOptions({ ...options, force: e.target.checked })}
                disabled={isJobActive}
                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm text-gray-700">Forcer le retraitement</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={options.skipBackup}
                onChange={(e) => setOptions({ ...options, skipBackup: e.target.checked })}
                disabled={isJobActive}
                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm text-gray-700">Ignorer le backup (deja fait)</span>
            </label>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        {!isJobActive ? (
          <button
            onClick={startBatch}
            disabled={isStarting || serviceAvailable === false || previewData?.totalCount === 0}
            className="flex-1 bg-emerald-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isStarting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Demarrage...
              </span>
            ) : (
              `Demarrer le traitement${options.limit ? ` (${options.limit} images)` : ''}`
            )}
          </button>
        ) : (
          <button
            onClick={cancelBatch}
            className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700"
          >
            Annuler le traitement
          </button>
        )}
      </div>

      {/* Job Progress */}
      {currentJob && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Progression</h2>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(currentJob.status)}`}>
              {getStatusText(currentJob.status)}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>{currentJob.progress.phase === 'backup' ? 'Sauvegarde' : 'Traitement'}</span>
              <span>{currentJob.progress.percentage.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className={`h-4 rounded-full transition-all duration-500 ${
                  currentJob.progress.phase === 'backup' ? 'bg-blue-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${currentJob.progress.percentage}%` }}
              />
            </div>
          </div>

          {/* Current Image */}
          {currentJob.progress.currentImage && isJobActive && (
            <p className="text-sm text-gray-600 mb-4 truncate">
              En cours: <span className="font-mono">{currentJob.progress.currentImage}</span>
            </p>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <div className="text-xl font-bold">{currentJob.progress.totalImages}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div>
              <div className="text-xl font-bold text-blue-600">{currentJob.progress.backedUp}</div>
              <div className="text-xs text-gray-500">Sauvegardes</div>
            </div>
            <div>
              <div className="text-xl font-bold text-emerald-600">{currentJob.progress.processed}</div>
              <div className="text-xs text-gray-500">Traites</div>
            </div>
            <div>
              <div className="text-xl font-bold text-gray-600">{currentJob.progress.skipped}</div>
              <div className="text-xs text-gray-500">Ignores</div>
            </div>
            <div>
              <div className="text-xl font-bold text-red-600">{currentJob.progress.errors}</div>
              <div className="text-xs text-gray-500">Erreurs</div>
            </div>
          </div>

          {/* Elapsed Time */}
          <p className="text-sm text-gray-500 mt-4">
            Temps ecoule: {getElapsedTime()}
          </p>

          {/* Error Details */}
          {currentJob.error && (
            <div className="mt-4 p-3 bg-red-50 rounded-lg">
              <p className="text-sm text-red-800">{currentJob.error}</p>
            </div>
          )}

          {currentJob.errorDetails && currentJob.errorDetails.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-red-800 mb-2">
                Images en erreur ({currentJob.errorDetails.length}):
              </p>
              <div className="max-h-32 overflow-y-auto text-xs font-mono bg-red-50 p-2 rounded">
                {currentJob.errorDetails.slice(0, 10).map((err, i) => (
                  <div key={i} className="text-red-700 truncate">
                    {err.image.split('/').pop()}: {err.error}
                  </div>
                ))}
                {currentJob.errorDetails.length > 10 && (
                  <div className="text-red-500">
                    ... et {currentJob.errorDetails.length - 10} autres erreurs
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">Pipeline de traitement</h3>
        <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
          <li>Sauvegarde des originaux (images-original/)</li>
          <li>Detourage IA (suppression arriere-plan)</li>
          <li>Redressement automatique si necessaire</li>
          <li>Amelioration: luminosite +5%, contraste +10%, nettete +15%</li>
          <li>Ajout d'une ombre portee subtile</li>
          <li>Normalisation a 1200x1200px avec 8% de marge</li>
          <li>Generation des miniatures (300px et 600px)</li>
        </ol>
      </div>
    </div>
  );
}
